import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Filter, Grid, List, Film, Radio, Clock, Calendar, X, ChevronDown, History } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category display names
const CATEGORY_LABELS = {
  'ÒPERA': 'Ópera',
  'BALLET': 'Ballet',
  'CONCERT': 'Concierto',
  'DOCUMENTAL': 'Documental',
  'PELÍCULA': 'Película'
};

const getCategoryLabel = (category) => CATEGORY_LABELS[category] || category;

// Helper function to check if a content's contracting date has passed
const isContentPast = (content) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  // For live content, check live_date
  if (content.is_live && content.live_date) {
    const liveDate = new Date(content.live_date);
    liveDate.setHours(0, 0, 0, 0);
    return liveDate < today;
  }
  
  // For encore content, check encore_end_date
  if (content.is_encore && content.encore_end_date) {
    const encoreEnd = new Date(content.encore_end_date);
    encoreEnd.setHours(0, 0, 0, 0);
    return encoreEnd < today;
  }
  
  return false;
};

const PastEventsPage = () => {
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  
  const [contents, setContents] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all'); // all, directo, encore
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  
  // Dropdown states
  const [showSectionDropdown, setShowSectionDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [contentsRes, sectionsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/content`),
        fetch(`${BACKEND_URL}/api/sections/active`)
      ]);
      
      if (contentsRes.ok) {
        const data = await contentsRes.json();
        // Filter only past events (directos and encores that have passed)
        const pastEvents = data.filter(content => {
          // Only consider directos and encores
          if (!content.is_live && !content.is_encore) return false;
          return isContentPast(content);
        });
        setContents(pastEvents);
      }
      
      if (sectionsRes.ok) {
        const data = await sectionsRes.json();
        setSections(data);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from contents
  const categories = [...new Set(contents.map(c => c.category).filter(Boolean))];

  // Filter contents
  const filteredContents = contents.filter(content => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!content.title?.toLowerCase().includes(query) && 
          !content.composer?.toLowerCase().includes(query)) {
        return false;
      }
    }
    
    // Section filter
    if (selectedSection !== 'all') {
      const section = sections.find(s => s.id === selectedSection);
      if (section) {
        const contentIds = section.contents?.map(c => c.content_id) || [];
        if (!contentIds.includes(content.id)) {
          return false;
        }
      }
    }
    
    // Category filter
    if (selectedCategory !== 'all' && content.category !== selectedCategory) {
      return false;
    }
    
    // Type filter
    if (selectedType !== 'all') {
      if (selectedType === 'directo' && !content.is_live) return false;
      if (selectedType === 'encore' && !content.is_encore) return false;
    }
    
    return true;
  });

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedSection('all');
    setSelectedCategory('all');
    setSelectedType('all');
  };

  const hasActiveFilters = searchQuery || selectedSection !== 'all' || selectedCategory !== 'all' || selectedType !== 'all';

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header 
        onOpenAuth={() => setAuthOpen(true)} 
        onOpenSearch={() => setSearchOpen(true)}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <History className="w-10 h-10 text-[#e4002b]" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">
              Eventos Pasados
            </h1>
          </div>
          <p className="text-white/60 text-lg max-w-2xl">
            Archivo de eventos en directo y encores cuyo período de contratación ha finalizado. 
            Estos contenidos están disponibles solo a modo de consulta.
          </p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            {/* Search */}
            <div className="relative flex-1 w-full lg:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <input
                type="text"
                placeholder="Buscar por título o compositor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:border-[#e4002b] focus:outline-none"
              />
            </div>

            {/* Filter Dropdowns */}
            <div className="flex flex-wrap gap-2 items-center">
              {/* Section Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowSectionDropdown(!showSectionDropdown);
                    setShowCategoryDropdown(false);
                    setShowTypeDropdown(false);
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                    selectedSection !== 'all' 
                      ? 'bg-[#e4002b]/20 border-[#e4002b] text-white' 
                      : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  <span className="text-sm">
                    {selectedSection === 'all' ? 'Sección' : sections.find(s => s.id === selectedSection)?.name}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {showSectionDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 py-1">
                    <button
                      onClick={() => { setSelectedSection('all'); setShowSectionDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedSection === 'all' ? 'text-[#e4002b]' : 'text-white/70'}`}
                    >
                      Todas las secciones
                    </button>
                    {sections.map(section => (
                      <button
                        key={section.id}
                        onClick={() => { setSelectedSection(section.id); setShowSectionDropdown(false); }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedSection === section.id ? 'text-[#e4002b]' : 'text-white/70'}`}
                      >
                        {section.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Category Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowCategoryDropdown(!showCategoryDropdown);
                    setShowSectionDropdown(false);
                    setShowTypeDropdown(false);
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                    selectedCategory !== 'all' 
                      ? 'bg-[#e4002b]/20 border-[#e4002b] text-white' 
                      : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                  }`}
                >
                  <Film className="w-4 h-4" />
                  <span className="text-sm">
                    {selectedCategory === 'all' ? 'Género' : getCategoryLabel(selectedCategory)}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {showCategoryDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 py-1">
                    <button
                      onClick={() => { setSelectedCategory('all'); setShowCategoryDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedCategory === 'all' ? 'text-[#e4002b]' : 'text-white/70'}`}
                    >
                      Todos los géneros
                    </button>
                    {categories.map(cat => (
                      <button
                        key={cat}
                        onClick={() => { setSelectedCategory(cat); setShowCategoryDropdown(false); }}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedCategory === cat ? 'text-[#e4002b]' : 'text-white/70'}`}
                      >
                        {getCategoryLabel(cat)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Type Filter */}
              <div className="relative">
                <button
                  onClick={() => {
                    setShowTypeDropdown(!showTypeDropdown);
                    setShowSectionDropdown(false);
                    setShowCategoryDropdown(false);
                  }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                    selectedType !== 'all' 
                      ? 'bg-[#e4002b]/20 border-[#e4002b] text-white' 
                      : 'bg-white/5 border-white/10 text-white/70 hover:border-white/30'
                  }`}
                >
                  <Radio className="w-4 h-4" />
                  <span className="text-sm">
                    {selectedType === 'all' ? 'Tipo' : 
                     selectedType === 'directo' ? 'Directo' : 'Encore'}
                  </span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                {showTypeDropdown && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl z-50 py-1">
                    <button
                      onClick={() => { setSelectedType('all'); setShowTypeDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 ${selectedType === 'all' ? 'text-[#e4002b]' : 'text-white/70'}`}
                    >
                      Todos los tipos
                    </button>
                    <button
                      onClick={() => { setSelectedType('directo'); setShowTypeDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center gap-2 ${selectedType === 'directo' ? 'text-[#e4002b]' : 'text-white/70'}`}
                    >
                      <span className="w-2 h-2 rounded-full bg-[#e4002b]" /> Directo
                    </button>
                    <button
                      onClick={() => { setSelectedType('encore'); setShowTypeDropdown(false); }}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center gap-2 ${selectedType === 'encore' ? 'text-[#e4002b]' : 'text-white/70'}`}
                    >
                      <span className="w-2 h-2 rounded-full bg-amber-500" /> Encore
                    </button>
                  </div>
                )}
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-2.5 text-white/60 hover:text-white text-sm"
                >
                  <X className="w-4 h-4" />
                  Limpiar
                </button>
              )}

              {/* View Mode Toggle */}
              <div className="flex items-center gap-1 ml-auto bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#e4002b] text-white' : 'text-white/50 hover:text-white'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-[#e4002b] text-white' : 'text-white/50 hover:text-white'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 text-white/50 text-sm">
            {filteredContents.length} {filteredContents.length === 1 ? 'evento pasado' : 'eventos pasados'}
            {hasActiveFilters && ' con los filtros aplicados'}
          </div>
        </div>
      </div>

      {/* Content Grid/List */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#e4002b] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredContents.length === 0 ? (
          <div className="text-center py-20">
            <History className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-white text-xl font-semibold mb-2">No hay eventos pasados</h3>
            <p className="text-white/50">Aún no hay eventos cuya fecha de contratación haya finalizado</p>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {filteredContents.map(content => (
              <Link
                key={content.id}
                to={`/content/${content.id}`}
                className="group"
              >
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-white/5">
                  <img
                    src={content.image || content.hero_image}
                    alt={content.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 grayscale-[30%]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                  
                  {/* Top badges */}
                  <div className="absolute top-2 left-2 flex flex-col gap-1">
                    {content.is_live && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-white/20 text-white/80 text-xs rounded font-bold">
                        <Radio className="w-3 h-3" /> DIRECTO
                      </span>
                    )}
                    {content.is_encore && !content.is_live && (
                      <span className="px-2 py-1 bg-amber-500/50 text-white text-xs rounded font-bold">
                        ENCORE
                      </span>
                    )}
                    {/* Past event badge */}
                    <span className="px-2 py-1 bg-black/70 text-white/70 text-xs rounded font-medium flex items-center gap-1">
                      <History className="w-3 h-3" /> PASADO
                    </span>
                  </div>
                  
                  <div className="absolute top-2 right-2">
                    <span className="px-2 py-1 bg-black/50 text-white/80 text-xs rounded">
                      {getCategoryLabel(content.category)}
                    </span>
                  </div>
                  
                  {/* Content info */}
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-semibold text-sm md:text-base group-hover:text-[#e4002b] transition-colors line-clamp-2">
                      {content.title}
                    </h3>
                    <p className="text-[#e4002b] text-xs md:text-sm mt-1">{content.composer}</p>
                    
                    {/* Date info */}
                    {content.is_live && content.live_date && (
                      <p className="text-white/50 text-xs mt-1 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(content.live_date).toLocaleDateString('es-ES')}
                      </p>
                    )}
                    {content.is_encore && content.encore_end_date && (
                      <p className="text-amber-400/70 text-xs mt-1">
                        Hasta {new Date(content.encore_end_date).toLocaleDateString('es-ES')}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="space-y-3">
            {filteredContents.map(content => (
              <Link
                key={content.id}
                to={`/content/${content.id}`}
                className="flex gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors group"
              >
                <div className="relative w-24 md:w-32 aspect-[3/4] flex-shrink-0 rounded-lg overflow-hidden">
                  <img
                    src={content.image || content.hero_image}
                    alt={content.title}
                    className="w-full h-full object-cover grayscale-[30%]"
                  />
                  {content.is_live && (
                    <div className="absolute top-1 left-1">
                      <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white/20 text-white/80 text-[10px] rounded font-bold">
                        <Radio className="w-2 h-2" /> DIRECTO
                      </span>
                    </div>
                  )}
                  {content.is_encore && !content.is_live && (
                    <div className="absolute top-1 left-1">
                      <span className="px-1.5 py-0.5 bg-amber-500/50 text-white text-[10px] rounded font-bold">
                        ENCORE
                      </span>
                    </div>
                  )}
                  <div className="absolute bottom-1 left-1">
                    <span className="px-1.5 py-0.5 bg-black/70 text-white/70 text-[10px] rounded font-medium flex items-center gap-0.5">
                      <History className="w-2 h-2" /> PASADO
                    </span>
                  </div>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-white font-semibold group-hover:text-[#e4002b] transition-colors">
                        {content.title}
                      </h3>
                      <p className="text-[#e4002b] text-sm">{content.composer}</p>
                    </div>
                    <span className="px-2 py-1 bg-white/10 text-white/60 text-xs rounded flex-shrink-0">
                      {getCategoryLabel(content.category)}
                    </span>
                  </div>
                  
                  <p className="text-white/50 text-sm mt-2 line-clamp-2">
                    {content.synopsis}
                  </p>
                  
                  <div className="flex items-center gap-4 mt-3 text-xs text-white/40">
                    {content.is_live && content.live_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Directo: {new Date(content.live_date).toLocaleDateString('es-ES')}
                      </span>
                    )}
                    {content.is_encore && content.encore_end_date && (
                      <span className="text-amber-400/70">
                        Encore hasta: {new Date(content.encore_end_date).toLocaleDateString('es-ES')}
                      </span>
                    )}
                    {content.theater && (
                      <span>{content.theater}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      <Footer />
      
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default PastEventsPage;
